import type { Ref } from 'vue'
import { DEFAULT_ADDRESS_COUNTRY } from '~~/layers/base/constants/defaultAddressCountry'
import type { AddressPayload } from '~~/layers/base/stores/contact.store'
import { formatAddressLines } from '~~/layers/base/utils/formatAddressLine'

/**
 * Full-page address chrome (list + create/edit). Profile uses this now so
 * Contact Manager can import the same map later without restyling.
 */
export const addressPageUi = {
    root: 'flex min-h-full flex-col bg-(--neutral-50) text-(--primary-700)',
    formBody: 'px-5 pb-[120px]',
    listBody: 'flex flex-col gap-4 px-5 pt-2 pb-9',
    list: 'm-0 flex list-none flex-col gap-3 p-0',
    addButton: 'h-auto justify-center gap-2.5 py-[18px] text-md font-medium',
    notFound: 'pt-4 text-center text-md text-(--neutral-700)',
    submit: 'mt-4 h-12 justify-center text-lg font-medium',
    submitError: 'text-md text-(--error-700)',
} as const

/**
 * Send / Contact Manager create-edit address drawers (`ContactDrawerHost`,
 * `ReturnAddressDrawerHost`): body padding, gold rule, section head, submit.
 */
export const addressDrawerUi = {
    body: 'space-y-3 px-5 pb-8',
    form: 'flex flex-col gap-4',
    divider: [
        'mx-auto my-1.5 h-px w-3/5',
        'bg-[linear-gradient(90deg,transparent,color-mix(in_srgb,var(--gold)_45%,transparent)_30%,color-mix(in_srgb,var(--gold)_60%,transparent)_50%,color-mix(in_srgb,var(--gold)_45%,transparent)_70%,transparent)]',
    ].join(' '),
    sectionHead: 'flex flex-col gap-1 -mt-1',
    sectionTitle:
        'm-0 font-serif text-2xl font-normal leading-(--leading-headings) tracking-tight text-(--primary-700)',
    sectionSub:
        'm-0 mt-0.5 font-sans text-md font-normal leading-(--leading-body) text-(--neutral-700)',
    statusMuted: 'text-md text-(--ui-text-muted)',
    alertError: 'text-md text-(--color-error-600)',
    submit: addressPageUi.submit,
} as const

/**
 * Shared Send-flow address field chrome (`RecipientField`, `ReturnAddressField`).
 * Each field adds its own root spacing and any picker-only keys.
 */
export const handwrittenAddressFieldUi = {
    fieldLabel: 'mb-2 uppercase text-sm tracking-(--tracking-label) text-(--ui-text-primary)',
    selectedWrap: 'cursor-pointer',
    /** Text-link colour for a light surface: the design system's gold on light, darkening on hover (TK-13409). */
    changeAddressLink: [
        'mt-3 flex h-auto w-full cursor-pointer justify-center px-0',
        'text-md font-medium',
        'text-(--gold-text-on-light) hover:text-(--gold-link-hover-on-light)',
    ].join(' '),
    lockedNote: 'text-sm text-(--ui-text-muted) mt-2 mb-1',
    /** MVP `.hm-picker-scroll` — create row + list; outer AdaptiveDrawer scrolls this. */
    pickerScroll: 'flex flex-col gap-2 px-5 pb-20 pt-0',
    pickerSectionEyebrow: 'mb-2 mt-1 text-(--secondary-700)',
    pickerStatus: 'm-0 px-3 py-6 text-center font-serif text-md italic text-(--neutral-500)',
} as const

/** Embedded `CraneAddressForm` spacing under Send address drawers. */
export const addressFormUi = {
    form: 'flex flex-col gap-2.5',
    nameGroup: 'flex flex-col gap-2.5',
} as const

/** Blank address draft for Crane address create screens (`CraneAddressForm` v-model). */
export function emptyAddressDraft(): AddressPayload {
    return {
        label: '',
        prefix: '',
        suffix: '',
        first_name: '',
        last_name: '',
        street_address_1: '',
        street_address_2: '',
        city: '',
        state: '',
        zip_code: '',
        country: DEFAULT_ADDRESS_COUNTRY,
    }
}

/**
 * POST body from `CraneAddressForm` — structured fields only (server rejects raw + structured).
 */
export function buildStructuredAddressCreateBody(value: AddressPayload): AddressPayload {
    const { raw_address_str: _, ...fields } = value
    return fields
}

/**
 * PATCH body from `CraneAddressForm` — structured fields only; clears legacy `raw_address_str`
 * so `mergeAddressPatchBody` + `validateAddress` stay xor-safe.
 */
export function buildStructuredAddressPatchBody(value: AddressPayload): Partial<AddressPayload> {
    const { raw_address_str: _, ...fields } = value
    return { ...fields, raw_address_str: null }
}

/**
 * PATCH body for contact recipient address edit (`submitEditContact`).
 * Raw-only rows send formatted `raw_address_str`; structured rows send every `CraneAddressForm` field
 * (addressee + postal) via {@link buildStructuredAddressPatchBody}.
 */
export function buildContactAddressPatchBody(
    addr: AddressPayload & { id?: string; is_default?: boolean },
    rawAddressIds: Set<string>
): Partial<AddressPayload> & { is_default?: boolean } {
    if (addr.id && rawAddressIds.has(addr.id)) {
        const raw = formatAddressLines({
            street_address_1: addr.street_address_1 ?? null,
            street_address_2: addr.street_address_2 ?? null,
            city: addr.city ?? null,
            state: addr.state ?? null,
            zip_code: addr.zip_code ?? null,
        }).join('\n')
        return { raw_address_str: raw || null, is_default: addr.is_default }
    }
    return {
        ...buildStructuredAddressPatchBody(addr),
        is_default: addr.is_default,
    }
}

const PLACES_CLEARABLE_KEYS = [
    'street_address_2',
    'city',
    'state',
    'zip_code',
    'country',
    'country_code',
] as const

/**
 * Merge a Google Places pick onto the current draft. Fields Places omitted are
 * cleared (`null` → `''`) so a new street does not keep a stale unit or city.
 * `street_address_1` is the exception: a locality-level pick has no street line,
 * so keep whatever the user typed rather than blanking it.
 */
export function mergePlacesAddressSelection(
    current: AddressPayload,
    picked: Partial<AddressPayload>
): AddressPayload {
    const updates: Partial<AddressPayload> = {}
    for (const key of PLACES_CLEARABLE_KEYS) {
        updates[key] = picked[key] ?? ''
    }
    if (picked.street_address_1 != null) {
        updates.street_address_1 = picked.street_address_1
    }
    return { ...current, ...updates }
}

/** Postal fields from the suggestion picker; names and label stay on the draft. */
export type AddressSuggestionFields = {
    street_address_1?: string | null
    street_address_2?: string | null
    city?: string | null
    state?: string | null
    zip_code?: string | null
}

/** Apply a picker choice onto the form draft. Names and label stay on `current`. */
export function mergeSuggestedAddress(
    current: AddressPayload,
    picked: AddressSuggestionFields
): AddressPayload {
    return {
        ...current,
        street_address_1: picked.street_address_1 ?? current.street_address_1,
        street_address_2: picked.street_address_2 ?? current.street_address_2,
        city: picked.city ?? current.city,
        state: picked.state ?? current.state,
        zip_code: picked.zip_code ?? current.zip_code,
    }
}

/** Write a validated address back into a bound form draft (picker pick or postal merge). */
export function mergeValidatedIntoDraft<T extends AddressPayload>(
    draft: Ref<T | null>,
    validated: AddressPayload
): void {
    if (!draft.value) return
    draft.value = { ...draft.value, ...validated }
}

/** Prefix / first / last / suffix line for envelope and checkout addressee resolution. */
export function formatAddressAddressee(fields: {
    prefix?: string | null
    suffix?: string | null
    first_name?: string | null
    last_name?: string | null
}): string {
    const parts = [fields.prefix, fields.first_name, fields.last_name]
        .filter(Boolean)
        .join(' ')
        .trim()
    return fields.suffix ? `${parts}, ${fields.suffix}` : parts
}
